import { useState, useMemo } from "react";
import { Filter, Search, ExternalLink, Edit, Database } from "lucide-react";
import { ORIENTATION_LABELS, FOUNDER_LABELS } from "../../lib/constants";
import { Badge } from "../common";

const sortOptions = [
  { value: "recent", label: "Recently Added", icon: "\u{1f550}" },
  { value: "name", label: "Name (A-Z)", icon: "\u{1f524}" },
  { value: "country", label: "Country", icon: "\u{1f30d}" },
  { value: "orientation", label: "Orientation", icon: "\u{1f3af}" },
  { value: "founder", label: "Founder Type", icon: "\u{1f465}" },
  { value: "launch_date", label: "Launch Date", icon: "\u{1f4c5}" },
];

export const DataExplorerTab = ({ apps, filteredApps: initialFilteredApps, filters, setFilters, searchTerm, setSearchTerm, stats, setSelectedApp, setShowCorrectionModal }) => {
  const [sortBy, setSortBy] = useState("recent");
  const [sortOrder, setSortOrder] = useState("desc");
  const [viewMode, setViewMode] = useState("grid");
  const [visibleCount, setVisibleCount] = useState(48);

  const sortedApps = useMemo(() => {
    const sorted = [...initialFilteredApps];
    sorted.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case "recent":
          comparison = new Date(b.created_at || b.launch_date || 0) - new Date(a.created_at || a.launch_date || 0);
          break;
        case "name":
          comparison = (a.product_name || "").localeCompare(b.product_name || "");
          break;
        case "country":
          comparison = (a.country || "ZZZ").localeCompare(b.country || "ZZZ");
          break;
        case "orientation": {
          const oo = { care: 1, utility: 2, creative: 3, extraction: 4, unknown: 5 };
          comparison = (oo[a.value_orientation] || 5) - (oo[b.value_orientation] || 5);
          break;
        }
        case "founder": {
          const fo = { female_only: 1, mixed: 2, male_only: 3, unknown: 4 };
          comparison = (fo[a.founder_composition] || 4) - (fo[b.founder_composition] || 4);
          break;
        }
        case "launch_date":
          comparison = new Date(b.launch_date || 0) - new Date(a.launch_date || 0);
          break;
        default:
          comparison = 0;
      }
      return sortOrder === "desc" ? comparison : -comparison;
    });
    return sorted;
  }, [initialFilteredApps, sortBy, sortOrder]);

  const sortedCountries = useMemo(() => Object.keys(stats?.country_distribution || {}).sort(), [stats?.country_distribution]);
  const toggleSortOrder = () => setSortOrder(prev => prev === "desc" ? "asc" : "desc");
  const visibleApps = useMemo(() => sortedApps.slice(0, visibleCount), [sortedApps, visibleCount]);
  const hasMore = visibleCount < sortedApps.length;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* How We Classify banner */}
      <div className="flex items-center justify-between bg-blue-500/[0.05] border border-blue-500/15 rounded-lg px-4 py-2.5">
        <p className="text-xs text-slate-400"><span className="text-blue-400 font-medium">Classification confidence</span> shown per app. Apps are classified by LLM analysis of name, tagline, and description.</p>
        <a href="#" onClick={(e) => { e.preventDefault(); window.open('/api/classification/explain', '_blank'); }} className="text-xs text-blue-400 hover:text-blue-300 font-medium whitespace-nowrap ml-3" data-testid="how-we-classify-link">How we classify &rarr;</a>
      </div>
      {/* Filters & Sort Controls */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filters:</span>
          </div>
          <select value={filters.value_orientation} onChange={(e) => setFilters(f => ({ ...f, value_orientation: e.target.value }))} className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm" data-testid="filter-orientation">
            <option value="">All Orientations</option>
            {Object.entries(ORIENTATION_LABELS).map(([key, label]) => (<option key={key} value={key}>{label}</option>))}
          </select>
          <select value={filters.founder_composition} onChange={(e) => setFilters(f => ({ ...f, founder_composition: e.target.value }))} className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm" data-testid="filter-founder">
            <option value="">All Founders</option>
            {Object.entries(FOUNDER_LABELS).map(([key, label]) => (<option key={key} value={key}>{label}</option>))}
          </select>
          <select value={filters.country} onChange={(e) => setFilters(f => ({ ...f, country: e.target.value }))} className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm" data-testid="filter-country">
            <option value="">All Countries</option>
            {sortedCountries.map(country => (<option key={country} value={country}>{country}</option>))}
          </select>
          <select value={filters.sub_type || ""} onChange={(e) => setFilters(f => ({ ...f, sub_type: e.target.value }))} className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm" data-testid="filter-subtype">
            <option value="">All Types</option>
            <option value="app">Apps</option>
            <option value="model">Models</option>
            <option value="demo">Demos / Spaces</option>
          </select>
          <select value={filters.industry || ""} onChange={(e) => setFilters(f => ({ ...f, industry: e.target.value }))} className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm" data-testid="filter-industry">
            <option value="">All Industries</option>
            {["Healthcare & Life Sciences", "Education & Research", "Finance & Insurance", "Creative & Media", "Retail & E-commerce", "Agriculture & Environment", "Manufacturing & Industrial", "Legal & Compliance", "Transportation & Logistics", "Security & Defense", "Real Estate & Construction", "Social Impact & Nonprofit", "Developer Tools & Infrastructure", "Entertainment & Gaming", "General Productivity", "Cross-Industry / Other"].map(ind => (
              <option key={ind} value={ind}>{ind}</option>
            ))}
          </select>
          <div className="flex-1 min-w-48">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search apps..." className="w-full pl-9 pr-3 py-1.5 bg-background border border-border rounded-lg text-sm" data-testid="search-input" />
            </div>
          </div>
        </div>
        {/* Sort Row */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-border">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Sort by:</span>
            <div className="flex flex-wrap gap-2">
              {sortOptions.map(option => (
                <button key={option.value} onClick={() => setSortBy(option.value)} data-testid={`sort-${option.value}`}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-all ${sortBy === option.value ? "bg-primary text-primary-foreground" : "bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground"}`}>
                  <span className="mr-1">{option.icon}</span>{option.label}
                </button>
              ))}
            </div>
            <button onClick={toggleSortOrder} data-testid="sort-order-toggle" className="p-2 hover:bg-muted rounded-lg transition-colors" title={sortOrder === "desc" ? "Descending" : "Ascending"}>
              {sortOrder === "desc" ? "\u2193" : "\u2191"}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Showing {visibleApps.length.toLocaleString()} of {stats?.total_apps?.toLocaleString() || sortedApps.length.toLocaleString()} apps</span>
            <div className="flex border border-border rounded-lg overflow-hidden">
              <button onClick={() => setViewMode("grid")} data-testid="view-grid" className={`p-2 ${viewMode === "grid" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
              </button>
              <button onClick={() => setViewMode("table")} data-testid="view-table" className={`p-2 ${viewMode === "table" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Grid View */}
      {viewMode === "grid" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleApps.map((app, idx) => {
            const hasDescription = !!app.description && app.description.length > 20;
            const hasTagline = !!app.tagline && app.tagline.length > 5;
            const isKnownFounder = app.founder_composition && app.founder_composition !== "unknown";
            const confidence = Math.min(95, 40 + (hasDescription ? 25 : 0) + (hasTagline ? 15 : 0) + (isKnownFounder ? 10 : 0) + (app.care_score > 0 || app.utility_score > 0 ? 5 : 0));
            return (
              <div key={app.id} className="bg-card border border-border rounded-lg p-4 card-hover animate-fade-in" style={{ animationDelay: `${Math.min(idx, 20) * 0.03}s` }}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold truncate">{app.product_name}</h4>
                    <p className="text-sm text-muted-foreground line-clamp-2">{app.tagline}</p>
                  </div>
                  {app.website_url && (
                    <a href={app.website_url} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-muted rounded-lg flex-shrink-0 ml-2">
                      <ExternalLink className="w-4 h-4 text-muted-foreground" />
                    </a>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <Badge type={app.value_orientation} variant="orientation" />
                  <Badge type={app.founder_composition} variant="founder" />
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-white/[0.08] text-muted-foreground/60">{confidence}% conf.</span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <span>{app.country || 'Unknown'}</span>
                    {app.launch_date && (<span className="text-muted-foreground/60">&bull; {app.launch_date.substring(0, 7)}</span>)}
                  </div>
                  <button onClick={() => { setSelectedApp(app); setShowCorrectionModal(true); }} className="flex items-center gap-1 hover:text-foreground" data-testid={`correct-btn-${app.id}`}>
                    <Edit className="w-3 h-3" />Wrong?
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table View */}
      {viewMode === "table" && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="text-left p-3 font-medium"><button onClick={() => { setSortBy("name"); toggleSortOrder(); }} className="flex items-center gap-1 hover:text-primary">App Name {sortBy === "name" && (sortOrder === "desc" ? "\u2193" : "\u2191")}</button></th>
                  <th className="text-left p-3 font-medium"><button onClick={() => { setSortBy("orientation"); toggleSortOrder(); }} className="flex items-center gap-1 hover:text-primary">Orientation {sortBy === "orientation" && (sortOrder === "desc" ? "\u2193" : "\u2191")}</button></th>
                  <th className="text-left p-3 font-medium"><button onClick={() => { setSortBy("founder"); toggleSortOrder(); }} className="flex items-center gap-1 hover:text-primary">Founders {sortBy === "founder" && (sortOrder === "desc" ? "\u2193" : "\u2191")}</button></th>
                  <th className="text-left p-3 font-medium"><button onClick={() => { setSortBy("country"); toggleSortOrder(); }} className="flex items-center gap-1 hover:text-primary">Country {sortBy === "country" && (sortOrder === "desc" ? "\u2193" : "\u2191")}</button></th>
                  <th className="text-left p-3 font-medium"><button onClick={() => { setSortBy("launch_date"); toggleSortOrder(); }} className="flex items-center gap-1 hover:text-primary">Launch {sortBy === "launch_date" && (sortOrder === "desc" ? "\u2193" : "\u2191")}</button></th>
                  <th className="text-left p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleApps.map((app, idx) => (
                  <tr key={app.id} className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${idx % 2 === 0 ? '' : 'bg-muted/10'}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2"><div className="min-w-0"><p className="font-medium truncate max-w-xs">{app.product_name}</p><p className="text-xs text-muted-foreground truncate max-w-xs">{app.tagline}</p></div></div>
                    </td>
                    <td className="p-3"><Badge type={app.value_orientation} variant="orientation" showTooltip={false} /></td>
                    <td className="p-3"><Badge type={app.founder_composition} variant="founder" showTooltip={false} /></td>
                    <td className="p-3 text-muted-foreground">{app.country || '\u2014'}</td>
                    <td className="p-3 text-muted-foreground">{app.launch_date?.substring(0, 7) || '\u2014'}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {app.website_url && (<a href={app.website_url} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-muted rounded-lg" title="Visit website"><ExternalLink className="w-4 h-4 text-muted-foreground" /></a>)}
                        <button onClick={() => { setSelectedApp(app); setShowCorrectionModal(true); }} className="p-1.5 hover:bg-muted rounded-lg" title="Suggest edit"><Edit className="w-4 h-4 text-muted-foreground" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sortedApps.length === 0 && (
        <div className="text-center py-12">
          <Database className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-muted-foreground">No apps found matching your criteria</p>
        </div>
      )}

      {hasMore && (
        <div className="text-center py-4">
          <button onClick={() => setVisibleCount(prev => prev + 48)} data-testid="load-more-btn"
            className="px-6 py-2.5 bg-muted hover:bg-muted/80 border border-border rounded-lg text-sm font-medium transition-colors">
            Load More ({(sortedApps.length - visibleCount).toLocaleString()} remaining)
          </button>
        </div>
      )}
    </div>
  );
};
